<template>
  <div>
    <v-card class="white elevation-3 ma-4">
      <!-- Use shared toolbar to match IncidentSystemSearch layout -->
      <toolbar
        :loading="loading"
        :export-loading="exportLoading"
        @search="search"
        @export="handleExport"
      />
    </v-card>

    <v-card class="white elevation-3 ma-4">
      <v-data-table
        :headers="headers"
        :items="incidents"
        :loading="!loading && loadingIncidents"
        :options.sync="options"
        :server-items-length="incidentsTotal"
        :footer-props="{ itemsPerPageOptions: [10,25,50] }"
        no-data-text="No data available"
        no-results-text="No data available"
        hide-default-header
        fixed-header
        class="header-nowrap apot-incidents-table"
      >
        <template v-slot:header="{ props: { headers } }">
          <thead
              class="v-data-table-header"
          >
          <tr
              class="fixed sections"
          >
            <th
                class="text-center sequence-number"
                rowspan="2"
            >
              Patient Care Report Number (eRecord.01)
            </th>
            <th
                class="text-left"
                :colspan="13"
            >
              APOT Data
            </th>
          </tr>
          <tr
              class="fields"
          >
            <th
                v-for="header in headers.filter(function (value) {
                  return value.hidden !== true;
                })"
                :key="header.text"
                :class="[
                  header.sortable ? 'sortable' : '',
                  header.value === options.sortBy[0] ? 'active' : '',
                  header.align === 'left' ? 'text-left' : '',
                  options.sortDesc[0] ? 'desc': 'asc'
                ]"
                :style="header.width ? 'min-width: ' + header.width + 'px;' : null"
                class="field-col text-left"
                @click="header.sortable ? changeSort(header.value) : null"
            >
              <span v-html="header.text"></span>
              <v-icon
                  v-if="header.sortable"
                  class="v-data-table-header__icon"
                  small
              >mdi-arrow-up</v-icon>
            </th>
          </tr>
          </thead>
        </template>

        <!-- Make Sequence Number clickable to open edit form -->
        <template v-slot:item.sequenceNumber="{ item }">
          <div class="first-col">
            <v-btn
                v-if="$can('viewIncidentDialog', 'incident')"
                icon
                class="float-left mr-1"
                @click="viewIncident(item)"
            >
              <v-icon>mdi-file-image</v-icon>
            </v-btn>

            <span class="float-left mt-2">
              <a
                  href="javascript:;"
                  @click.prevent.stop="viewIncident(item)"
              >
                  <b>
                    {{ item.sequenceNumber }}
                  </b>
              </a>
            </span>

            <v-btn
                icon
                class="float-right"
                @click="openEditForm(item)"
            >
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.incidentDateTime="{ item }">
          <span>{{ item.incidentDateTime | datetime }}</span>
        </template>
        <template v-slot:item.dateOfBirth="{ item }">
          <span>{{ item.dateOfBirth | date }}</span>
        </template>
        <template v-slot:item.arrivedAtDestination="{ item }">
          <span>{{ item.arrivedAtDestination | datetime }}</span>
        </template>
        <template v-slot:item.destinationTransferOfCare="{ item }">
          <span>{{ item.destinationTransferOfCare | datetime }}</span>
        </template>
        <template v-slot:item.calculatedApot="{ item }">
          <span>{{ item.calculatedApot }}</span>
        </template>
        <template v-slot:item.signatureDateTime="{ item }">
          <span>{{ item.signatureDateTime | datetime }}</span>
        </template>
        <template v-slot:item.unitBackInService="{ item }">
          <span>{{ item.unitBackInService | datetime }}</span>
        </template>
        <template v-slot:item.facilityCode="{ item }">
          <span>{{ item.facilityCode || '—' }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit Form Dialog -->
    <edit-apot-incident-form
      ref="editForm"
      @saved="handleSaved"
    />

    <view-incident-popup
        ref="viewIncidentPopup"
        :show-flag-btn="false"
        @view-incident="viewIncident"
    />
  </div>

</template>

<script>
import _ from 'lodash';
import { SET_TITLE } from '@/store/mutations';
import moment from 'moment';
import Toolbar from '@/components/ApotIncidents/Toolbar';
import EditApotIncidentForm from '@/components/ApotIncidents/EditApotIncidentForm';
import apotIncidents from '@/api/apotIncidents';
import ViewIncidentPopup from '@/components/Incidents/ViewIncidentPopup';
import fileDownload from 'js-file-download';

export default {
  components: {
    ViewIncidentPopup,
    Toolbar,
    EditApotIncidentForm,
  },
  data() {
    return {
      loading: false,
      exportLoading: false,
      incidents: [],
      incidentsTotal: 0,
      loadingIncidents: false,
      options: {
        page: 1,
        sortBy: [],
        itemsPerPage: 25,
      },
      filters: {
        dateFrom: '',
        dateTo: '',
        sequenceNumbers: '',
        incidentNumbers: '',
        firstName: '',
        lastName: '',
      },
      headers: [
        {
          text: 'Patient Care Report Number (eRecord.01)', value: 'sequenceNumber', sortable: false, hidden: true,
        },
        { text: 'Incident Number (eResponse.03)', value: 'incidentNumber', sortable: false },
        { text: 'Incident Date (eTimes.01)', value: 'incidentDateTime', sortable: false },
        { text: 'Facility Name (eDisposition.01)', value: 'facilityName', sortable: false },
        { text: 'Facility Code (eDisposition.02)', value: 'facilityCode', sortable: false },
        { text: 'Patient First Name (ePatient.03)', value: 'firstName', sortable: false },
        { text: 'Patient Last Name (ePatient.02)', value: 'lastName', sortable: false },
        { text: 'Patient Date Of Birth (ePatient.17)', value: 'dateOfBirth', sortable: false },
        { text: 'Patient Gender (ePatient.13)', value: 'gender', sortable: false },
        { text: 'Patient Arrived at Destination (eTimes.11)', value: 'arrivedAtDestination', sortable: false },
        { text: 'Destination Patient Transfer of Care (eTimes.12)', value: 'destinationTransferOfCare', sortable: false },
        { text: 'Calculated APOT', value: 'calculatedApot', sortable: false },
        { text: 'Signature Date/Time (eOther.19)', value: 'signatureDateTime', sortable: false },
        { text: 'Unit Back in Service (eTimes.13)', value: 'unitBackInService', sortable: false },
      ],
    };
  },
  watch: {
    options: {
      deep: true,
      handler() {
        // options changed (page or itemsPerPage), refetch current list
        this.searchIncidents(this.options);
      },
    },
  },
  mounted() {
    this.$store.commit(SET_TITLE, 'APOT Reconciliation');
    // If query params exist, restore filters
    this.restoreFromRoute();
    this.searchIncidents(this.options);
  },
  methods: {
    /**
     * Handle toolbar search: normalize and trigger list loading
     */
    async search(toolbarFilters) {
      this.filters = toolbarFilters;

      this.options.page = 1;
      try {
        this.loading = true;
        await this.searchIncidents(this.options);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Prepare params for searching.
     *
     * @param {Object} pagination - Pagination data.
     *
     * @returns {*}
     */
    prepareParams(pagination = {}) {
      const params = _.cloneDeep(this.filters);
      params.page = pagination ? pagination.page : 1;
      params.perPage = pagination ? pagination.itemsPerPage : 25;

      return params;
    },
    /**
     * Load incidents list with pagination
     */
    async searchIncidents(pagination = {}) {
      try {
        this.loadingIncidents = true;
        const params = this.prepareParams(pagination);
        const res = await apotIncidents.getApotIncidents(params);
        this.incidents = Array.isArray(res.results) ? res.results : [];
        this.incidentsTotal = res.pagination?.total || 0;
      } catch (e) {
        console.error('Failed to fetch APOT incidents', e);
      } finally {
        this.loadingIncidents = false;
      }
    },
    restoreFromRoute() {
      const q = this.$route.query || {};
      this.filters = {
        dateFrom: q.dateFrom || '',
        dateTo: q.dateTo || '',
        sequenceNumbers: q.sequenceNumbers || '',
        incidentNumbers: q.incidentNumbers || '',
        firstName: q.firstName || '',
        lastName: q.lastName || '',
      };
      if (q.page) this.options.page = Number(q.page);
      if (q.perPage) this.options.itemsPerPage = Number(q.perPage);
    },
    formatDateTime(value, mode = 'datetime') {
      if (!value) return '—';
      const m = moment(value);
      if (!m.isValid()) return '—';
      return mode === 'date' ? m.format('MM/DD/YYYY') : m.format('MM/DD/YYYY h:mm A');
    },
    /**
     * Handle export action: download XLSX file.
     */
    async handleExport(toolbarFilters) {
      try {
        this.exportLoading = true;
        // Prepare params similar to search but without pagination
        const params = {
          dateRangeFrom: toolbarFilters.dateRangeFrom || null,
          dateRangeTo: toolbarFilters.dateRangeTo || null,
          rangeBy: toolbarFilters.rangeBy || null,
          sequenceNumbers: toolbarFilters.sequenceNumbers || null,
          incidentNumbers: toolbarFilters.incidentNumbers || null,
          firstName: toolbarFilters.firstName || null,
          lastName: toolbarFilters.lastName || null,
        };

        // Call export API and get blob
        const blob = await apotIncidents.exportApotIncidents(params);
        fileDownload(blob, 'apot-incidents.xlsx');
      } catch (e) {
        console.error('Failed to export APOT incidents', e);
        this.$store.commit('showError', 'Failed to export data. Please try again.');
      } finally {
        this.exportLoading = false;
      }
    },
    /**
     * Open edit form when user clicks on a table row.
     * @param {Object} incident - The clicked incident row data
     */
    openEditForm(incident) {
      this.$refs.editForm.show(incident);
    },
    /**
     * Handle saved event from edit form - reload the grid.
     */
    async handleSaved() {
      await this.searchIncidents(this.options);
    },

    viewIncident(incident) {
      const previousIncident = null;
      const nextIncident = null;
      const options = {
        showViewIncidentLink: true,
        showActions: true,
        showNonRegularActions: false,
      };
      this.$refs.viewIncidentPopup.show(
        { id: incident.incidentId, II_SequenceNumber: incident.sequenceNumber },
        options,
        null,
        previousIncident,
        nextIncident,
      );
    },
  },
};
</script>

<style lang="scss">
.apot-incidents-table {
  .v-data-table__wrapper {
    height: calc(100vh - 300px);
  }
  &.clickable-rows tbody tr {
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
  }

  .sequence-number {
    z-index: 100 !important;
  }
  .field-col {
    white-space: nowrap;
  }
  table > tbody > tr > td:nth-child(1),
  table > thead > tr.fixed > th:nth-child(1) {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 1;
    background: #f5f5f5;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  table > thead > tr.fixed > th:nth-child(1) {
    z-index: 3;
    width: 240px;
  }
  table > thead tr.sections th {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    background: #f5f5f5 !important;
    height: 30px;
  }
  table > thead tr.fields th{
    top: 30px !important;
  }
  table > thead tr.sections th:nth-last-child(1) {
    border-right: none;
  }
  .first-col {
    width: 240px;
    white-space: nowrap;
    button {
      margin-left: 10px;
      i {
        cursor: pointer;
      }
    }
  }
}
</style>
